/** @jsxImportSource theme-ui */

import React, { useEffect, useState, useContext } from "react"
import { useTranslation } from "react-i18next"
import styled from "@emotion/styled"
import { css } from "@emotion/react"
import { Flex, Box, Image } from "theme-ui"
import { saveAs } from "file-saver"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faSpinner,
  faImage,
  faCirclePlay,
  faFile,
  faMicrophone,
  faPlay,
  faNoteSticky,
} from "@fortawesome/free-solid-svg-icons"
import ExternalLinkOutWrapper from "../../ExternalLinkOutWrapper/ExternalLinkOutWrapper"
import { WoztellContext } from "../../../contexts/WoztellContextProvider"
import { resourceTemplateTheme } from "../../../stylesheets/theme"

const FILE_API_PATH = process.env.REACT_APP_FILE_API_PATH

const Error = styled.div`
  font-size: ${(props) => props.theme.textSm};
  color: ${(props) => props.theme.error};
  padding: 8px;
  max-width: 212px;
`

const AudioTemplate = styled.div`
  display: flex;
  
  >.duration-wrapper {
    display: flex;
    justify-content: center;
    align-items: start;
    flex-direction: column;
    padding: 0 8px;
    width: 136px;
    box-sizing: border-box;
    position: relative;

    >.line {
      background: #ababab;
      width: 100%;
      height: 1px;
    }

    >.title {
      position: absolute ;
      top: 8px;
      left: 22px;
    }
  }
`

const ImageUrlContainer = styled.a`
  width: 100%;
  display: block;
  background-color: white;
  margin: 0 auto;
  border-radius: 6px;
`

const StyledAudio = styled.audio`
  @media screen and (max-width: 500px) {
    max-width: 212px;
  }
`

const ClickToDownloadWrapper = styled.div`
  ${p => p.isReply && css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    cursor: pointer;
  `}
`

const MaxWidthBox = styled(Box)`
  @media screen and (max-width: 500px) {
    max-width: 210px !important;
  }
  ${(props) => {
    if (props.isWhatsappCloudThread && props.from === "BOT") {
      return css`
        background: ${resourceTemplateTheme.bot.whatsapp.colors.textBackgroundWhite};
      `
    }
    return css``
  }}
`

const checkBrowserSupportedType = ({ type }) => {
  if (!type) return []
  const ele = document.createElement(type)
  const typesToCheck = {
    audio: ["audio/mp4", "audio/mpeg", "audio/webm", "audio/aac"],
  }
  if (!typesToCheck[type]) return []

  const supportedTypes = []

  typesToCheck[type].forEach((t) => {
    const isSupported = ele.canPlayType(t)
    if (isSupported) {
      supportedTypes.push({
        type: t,
      })
    }
  })

  return supportedTypes
}

const MediaDownload = ({
  fileId,
  onLoadUrl,
  channelId,
  type,
  isConversation = false,
  from,
  isWhatsappCloudThread,
  isReply = false,
}) => {
  const { payload, signedContext, loading: contextLoading } = useContext(WoztellContext)
  const [auth, setAuth] = useState(null)
  const [loading, setLoading] = useState(false)
  const [fileUrl, setFileUrl] = useState(null)
  const [fileType, setFileType] = useState(null)
  const [error, setError] = useState(null)
  const { t } = useTranslation("common")

  useEffect(() => {
    if (!contextLoading && payload && signedContext) {
      setAuth({
        payload,
        signedContext,
      })
      checkFile({
        fileId,
        channelId,
        auth: {
          payload,
          signedContext,
        }
      })
    } else if (!contextLoading) {
      checkFile({
        fileId,
        channelId,
      })
    }
  }, [contextLoading, JSON.stringify(payload), signedContext])

  const fetchFile = async ({
    fileId,
    channelId,
    auth,
    type,
  }) => {
    setLoading(true)
    let apiUrl = `${FILE_API_PATH}/external-file-id/${fileId}?channel=${channelId}`
    if (/^[0-9a-f]{24}$/.test(fileId)) {
      // woztell file ID
      apiUrl = `${FILE_API_PATH}/${fileId}`
    }
    if (type === "audio") {
      const browserSupportedType = checkBrowserSupportedType({ type: "audio" })
      apiUrl = `${apiUrl}?expected_media_types=${JSON.stringify(browserSupportedType)}`
    }
    let headers
    if (auth?.payload && auth?.signedContext) {
      headers = {
        "X-Woztell-Payload": JSON.stringify(auth.payload),
        "X-Woztell-SignedContext": auth.signedContext,
      }
    }
    let contentType
    let filename
    const fileBlob = await fetch(apiUrl, {
      headers,
      credentials: "include",
    }).then((res) => {
      contentType = res.headers.get("content-type")
      const header = res.headers.get("Content-Disposition")
      const parts = header.split(";")
      filename = parts[1].split("=")[1]
      return res.blob()
    })
    const url = URL.createObjectURL(fileBlob)
    setFileUrl(url)
    setFileType(contentType)
    setLoading(false)
    return {
      contentType,
      url,
      filename,
    }
  }

  const checkFile = async ({
    fileId,
    channelId,
    auth,
    type,
  }) => {
    setLoading(true)
    let headers
    if (auth?.payload && auth?.signedContext) {
      headers = {
        "X-Woztell-Payload": JSON.stringify(auth.payload),
        "X-Woztell-SignedContext": auth.signedContext,
      }
    }
    try {
      let apiUrl = `${FILE_API_PATH}/external-file-id/${fileId}/stat?channel=${channelId}`
      if (/^[0-9a-f]{24}$/.test(fileId)) {
        // woztell file ID
        apiUrl = `${FILE_API_PATH}/${fileId}/stat`
      }
      const fileInfoRes = await fetch(apiUrl, {
        headers,
        credentials: "include",
      }).then(res => res.json())

      if (fileInfoRes.ok) {
        if (fileInfoRes.file?.size <= 2048000) {
          await fetchFile({ fileId, channelId, auth, type })
          setLoading(false)
        } else {
          setFileType(fileInfoRes.file?.fileType)
          setLoading(false)
        }
      } else {
        throw new Error(fileInfoRes?.error?.message)
      }
    } catch (error) {
      setLoading(false)
      setError(error.message)
    }
  }

  useEffect(() => {
    if (fileUrl) {
      if (onLoadUrl) {
        onLoadUrl()
      }
    }
  }, [fileUrl])

  const renderContent = () => {
    if (error) {
      return (
        <Flex sx={{ height: "100%", alignItems: "center", justifyContent: "center" }}>
          <Error>[{error?.code}] {error?.message}</Error>
        </Flex>
      )
    }
    if (!fileUrl) {
      let placeholder
      const downloadFileComponent = (
        <div
          style={{
            fontSize: "0.83333em",
            color: isConversation ? "#ffffff" : "#626262",
            paddingTop: "4px",
            fontWeight: "bold"
          }}
        >
          {t?.("message_types.media_download.click_to_download")}
        </div>
      )
      switch (type?.toLowerCase()) {
        case "image":
          placeholder = (
            <ClickToDownloadWrapper isReply={isReply}>
              <FontAwesomeIcon icon={faImage} style={{ fontSize: "40px", color: "#ababab", marginBottom: "8px" }} />
              {downloadFileComponent}
            </ClickToDownloadWrapper>
          )
          break
        case "sticker":
          placeholder = (
            <ClickToDownloadWrapper isReply={isReply}>
              <FontAwesomeIcon icon={faNoteSticky} style={{ fontSize: "40px", color: "#ababab", marginBottom: "8px" }} />
              {downloadFileComponent}
            </ClickToDownloadWrapper>
          )
          break
        case "video":
          placeholder = (
            <ClickToDownloadWrapper isReply={isReply}>
              <FontAwesomeIcon icon={faCirclePlay} style={{ fontSize: "40px", color: "#ababab", marginBottom: "8px" }} />
              {downloadFileComponent}
            </ClickToDownloadWrapper>
          )
          break
        case "audio":
          placeholder = (
            <AudioTemplate>
              <FontAwesomeIcon
                style={{ fontSize: "16px", color: "#ababab" }}
                className="audio-icon"
                icon={faPlay}
              />
              <div className="duration-wrapper">
                <div className="line" />
                <div className="title" style={{ fontSize: "0.83333em", color: "#626262", paddingTop: "4px", fontWeight: "bold" }}>{t?.("message_types.media_download.click_to_download")}</div>
              </div>
              <FontAwesomeIcon
                style={{ fontSize: "16px", color: "#ababab" }}
                className="audio-icon"
                icon={faMicrophone}
              />
            </AudioTemplate>
          )
          break
        case "file":
          placeholder = (
            <ClickToDownloadWrapper isReply={isReply}>
              <FontAwesomeIcon
                className="icon"
                icon={faFile}
                style={{ fontSize: "40px", color: "#ababab", marginBottom: "8px" }}
              />
              {downloadFileComponent}
            </ClickToDownloadWrapper>
          )
          break
        default:
          break
      }
      return (
        <Flex
          sx={{
            height: "100%",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer"
          }}
          onClick={() => {
            if (type?.toLowerCase() === "file") {
              fetchFile({
                fileId,
                channelId,
                auth,
                type
              }).then(({ url, filename }) => {
                saveAs(url, filename)
              })
            } else {
              fetchFile({
                fileId,
                channelId,
                auth,
                type
              })
            }
          }}
        >
          {loading ? (
            <FontAwesomeIcon spin icon={faSpinner} style={{ fontSize: "24px", color: "#ababab" }} />
          ) : placeholder}
        </Flex>
      )
    }
    if (fileUrl && /image/i.test(fileType)) {
      if (isConversation) {
        return (
          <ExternalLinkOutWrapper url={fileUrl} displayLinkOutToLeft={from === "BOT"}>
            <ImageUrlContainer
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Image
                className="media-download-image"
                sx={{
                  width: "100%",
                  verticalAlign: "bottom",
                  borderRadius: "6px",
                  objectFit: "cover",
                }}
                src={fileUrl}
              />
            </ImageUrlContainer>
          </ExternalLinkOutWrapper>
        )
      }
      return (
        <Image
          className="media-download-image"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "6px",
          }}
          src={fileUrl}
        />
      )
    } else if (fileUrl && /video/i.test(fileType)) {
      return (
        <video
          className="media-download-video"
          sx={{ width: "100%", height: "100%", objectFit: "contain" }}
          src={fileUrl}
          controls
        ></video>
      )
    } else if (fileUrl && /audio/i.test(fileType)) {
      return (
        <StyledAudio className="media-download-audio" controls>
          <source src={fileUrl} />
        </StyledAudio>
      )
    } else if (fileUrl) {
      return (
        <a href={fileUrl} target="_blank" rel="noopener noreferrer">
          <div
            className="media-download-file"
            style={{
              display: "flex",
              height: "100%",
              width: "100%",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column"
            }}
          >
            <FontAwesomeIcon
              className="icon"
              icon={faFile}
              style={{ fontSize: "40px", color: "#ababab", marginBottom: "8px" }}
            />
            <div className="title"
              title={fileUrl}
              style={{
                fontSize: "0.83333em",
                color: "#626262",
                paddingTop: "4px",
                fontWeight: "bold",
                textOverflow: "ellipsis",
                overflow: "hidden",
                textAlign: "center",
                wordBreak: "break-all"
              }}
            >
              {fileUrl.split("/")[fileUrl.split("/").length - 1]}
            </div>
          </div>
        </a>
      )
    }
  }

  return (
    <MaxWidthBox isWhatsappCloudThread={isWhatsappCloudThread} from={from} className="media-download-container" style={{ width: "100%", height: "100%" }}>
      {renderContent()}
    </MaxWidthBox>
  )
}

const MediaDownloadWrapper = ({
  isConversation,
  from,
  fileId,
  channelId,
  isWhatsappCloudThread,
  isReply
}) => ({ ...props }) => (
  <MediaDownload
    {...props}
    isConversation={isConversation}
    from={from}
    fileId={fileId}
    channelId={channelId}
    isWhatsappCloudThread={isWhatsappCloudThread}
    isReply={isReply}
  />
)


export default MediaDownloadWrapper
